import { useCallback, useMemo } from "react";

import { batchOperation } from "shared/constants/allocationsEnums";
import orderBy from "lodash/orderBy";
import { useAllocations } from "features/allocations/api/useAllocations";
import { useApi } from "shared/services/api/useApi";

export const useCustomGroups = () => {
    const { getDataAsync, postData, putData, deleteData } = useApi();
    const { getAllocationsUsersIds } = useAllocations();

    const getMyCustomGroups = useCallback(async (_userId) => {

        const payload = await getDataAsync("getCustomGroupsSelf");
        const data = await payload.json();
        const orderResult = orderBy(data, [(user) => user.Name.toLowerCase()], ["asc"]);

        return orderResult;
    }, [getDataAsync]);

    const getAllCustomGroups = useCallback(async () => {
        const payload = await getDataAsync("getCustomGroups");
        const data = await payload.json();
        const orderResult = orderBy(data, [(user) => user.Name.toLowerCase()], ["asc"]);

        return orderResult;
    }, [getDataAsync]);

    const getCustomGroup = useCallback(async (urlParams) => {
        const payload = await getDataAsync("getCustomGroup", { urlParams: urlParams });
        const data = await payload.json();

        return data;
    }, [getDataAsync]);

    const validateCustomGroup = useCallback(async (urlParams) => {
        return getDataAsync("validateCustomGroup", { params: urlParams });
    }, [getDataAsync]);

    const createCustomGroup = useCallback(async (
        name,
        description,
        userIds,
        selectedAll,
        summaryQuery
    ) => {
        if (selectedAll) {
            userIds = await getAllocationsUsersIds(summaryQuery);
        }

        const body = {
            name: name,
            description: description,
            userIds: userIds,
        };

        const payload = await postData("createCustomGroup", {
            body: body,
        });

        return payload.json();
    }, [postData, getAllocationsUsersIds]);

    const updateCustomGroup = useCallback(async (body) => {
        return putData("updateCustomGroup", {
            body: body,
        });
    }, [putData]);

    const deleteCustomGroup = useCallback(async (urlParams) => {
        return deleteData("deleteCustomGroup", {
            urlParams: urlParams,
        });
    }, [deleteData]);

    const addUserToCustomGroup = useCallback(async (
        customGroupId,
        usersIds,
        skipDuplicatedUsers
    ) => {
        const urlParams = [
            {
                name: "$customgroupid",
                value: customGroupId,
            },
        ];

        const result = postData("staffingCustomGroup", {
            body: usersIds,
            urlParams: urlParams,
            params: {
                operation: batchOperation.add,
                skipDuplicatedUsers: skipDuplicatedUsers,
            },
        });

        return result;
    }, [postData]);

    const removeUserFromCustomGroup = useCallback(async (customGroupId, usersIds) => {
        const urlParams = [
            {
                name: "$customgroupid",
                value: customGroupId,
            },
        ];

        return postData("staffingCustomGroup", {
            body: usersIds,
            urlParams: urlParams,
            params: {
                operation: batchOperation.remove,
            },
        });
    }, [postData]);

    return useMemo(() => {
        return {
            getMyCustomGroups,
            getAllCustomGroups,
            getCustomGroup,
            validateCustomGroup,
            createCustomGroup,
            updateCustomGroup,
            deleteCustomGroup,
            addUserToCustomGroup,
            removeUserFromCustomGroup,
        };
    }, [
        getMyCustomGroups,
        getAllCustomGroups,
        getCustomGroup,
        validateCustomGroup,
        createCustomGroup,
        updateCustomGroup,
        deleteCustomGroup,
        addUserToCustomGroup,
        removeUserFromCustomGroup,
    ]);
};
